// Small non-interactive-ish map for entity pages: renders lines/points and
// fits bounds to them.
import { useEffect, useRef } from "react";
import maplibregl, { LngLatBounds } from "maplibre-gl";
import { featureCollection, type Feature } from "../lib/geo";
import { useResolvedTheme } from "../lib/theme";
import { basemapStyle } from "./basemap";
import { COLORS } from "./layers";

function extendBounds(bounds: LngLatBounds, coords: unknown): void {
  if (!Array.isArray(coords)) return;
  if (coords.length >= 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
    bounds.extend([coords[0] as number, coords[1] as number]);
    return;
  }
  for (const c of coords) extendBounds(bounds, c);
}

export function MiniMap({ features, className }: { features: (Feature | null)[]; className?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const present = features.filter(Boolean) as Feature[];
  const theme = useResolvedTheme();

  useEffect(() => {
    if (!containerRef.current || !present.length) return;
    const bounds = new LngLatBounds();
    for (const f of present) extendBounds(bounds, f.geometry.coordinates);
    if (bounds.isEmpty()) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: basemapStyle(theme),
      bounds,
      fitBoundsOptions: { padding: 40, maxZoom: 14 },
      attributionControl: { compact: true },
      cooperativeGestures: true,
    });
    map.on("load", () => {
      map.addSource("entity", { type: "geojson", data: featureCollection(present) });
      map.addLayer({
        id: "entity-fill",
        type: "fill",
        source: "entity",
        filter: ["==", ["geometry-type"], "Polygon"],
        paint: { "fill-color": COLORS.region, "fill-opacity": 0.1 },
      });
      map.addLayer({
        id: "entity-line",
        type: "line",
        source: "entity",
        filter: ["in", ["geometry-type"], ["literal", ["LineString", "MultiLineString", "Polygon", "MultiPolygon"]]],
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": COLORS.route, "line-width": 3, "line-opacity": 0.85 },
      });
      map.addLayer({
        id: "entity-point",
        type: "circle",
        source: "entity",
        filter: ["==", ["geometry-type"], "Point"],
        paint: {
          "circle-color": COLORS.place,
          "circle-radius": 7,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
      });
    });
    return () => map.remove();
    // Re-create only when the feature set materially changes or the theme flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(present.map((f) => f.properties.id ?? f.geometry.type)), theme]);

  if (!present.length) return null;
  return <div ref={containerRef} className={className ?? "h-64 w-full overflow-hidden rounded-xl border shadow-card"} />;
}
